import { CircularLoader } from '@modules/common/components/CircularLoader';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useUpdateEditedPredictions } from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import CheckRoundedIcon from '@mui/icons-material/CheckRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import EditRoundedIcon from '@mui/icons-material/EditRounded';
import { IconButton, Stack, Typography } from '@mui/material';
import { FeedbackLabelType } from 'v2/core/entities/feedback';

interface SentimentPredictionLabelRightActionsProps {
  recordID: string;
  isEditPredictionMode: boolean;
  setEditPredictionMode: (isEditPredictionsMode: boolean) => void;
}

export const SentimentPredictionLabelRightActions = ({
  recordID,
  isEditPredictionMode,
  setEditPredictionMode,
}: SentimentPredictionLabelRightActionsProps) => {
  const { showSuccessNotification } = useNotificationContext();
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);

  const { isLoading, updateEditedPredictions } =
    useUpdateEditedPredictions(recordID);

  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );

  const isSentimentPredictionEdited = recordInEditMode?.editPredictions?.some(
    (record) => record.type === FeedbackLabelType.Sentiment,
  );

  const onEditPrediction = async () => {
    updateEditedPredictions({
      onAfterEditedPredictions: () => {
        setEditPredictionMode(false);
        showSuccessNotification({
          contentComponent: () => (
            <Stack spacing={1} mt={-0.5}>
              <Typography variant="subtitle1">Thanks for the edit!</Typography>
              <Typography variant="body2">
                We'll use it to better categorize your customer feedback. This
                change may take up to 60 mins to reflect on your organization's
                Taxonomy.
              </Typography>
            </Stack>
          ),
          props: {},
        });
      },
    });
  };

  const onEditClick = () => {
    dispatch({
      type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
      payload: {
        id: recordID,
        editPredictions: recordInEditMode?.editPredictions ?? [],
      },
    });
    setEditPredictionMode(true);
  };

  const cancelEditPredictionMode = () => {
    dispatch({
      type: FeedbackOnFeedbackActionType.RESET_RECORD_IN_EDIT_MODE,
      payload: {},
    });
    setEditPredictionMode(false);
  };

  if (!isEditPredictionMode) {
    return (
      <IconButton size="small" onClick={onEditClick}>
        <EditRoundedIcon
          color="primary"
          sx={{
            fontSize: '14px',
          }}
        />
      </IconButton>
    );
  }

  if (isLoading) {
    return <CircularLoader size={22} sx={{ mr: 2 }} />;
  }

  return (
    <Stack direction="row">
      <IconButton
        disabled={!isSentimentPredictionEdited}
        size="small"
        onClick={onEditPrediction}
      >
        <CheckRoundedIcon fontSize="small" />
      </IconButton>
      <IconButton size="small" onClick={cancelEditPredictionMode}>
        <CloseRoundedIcon fontSize="small" />
      </IconButton>
    </Stack>
  );
};
